import { Injectable, signal } from "@angular/core";
import { operationApresRepli } from "app/shared/utils/operation-depliee.utils";

/**
 * **Quel essai de développement est ouvert à l'écran** — rien de plus.
 *
 * ## Pourquoi ce service existe
 *
 * Un contrôle de **marquage** compare la saisie de l'opérateur au numéro de lot et à la date limite. Dans
 * un essai, ces deux données ne viennent pas d'un lot de production mais de l'identité du **projet** de
 * recette : il faut donc que l'enregistrement passe par la route qui les fournit
 * (`POST recette/essais/:essaiId/controles/answer`). Sinon la comparaison n'a rien à lire, et le contrôle
 * ressort « incomplet » sans que personne ne sache pourquoi.
 *
 * ## Pourquoi un contexte, et pas une entrée de composant
 *
 * ⚠️ Le contrôle est affiché au bout d'une chaîne de **cinq** composants (le runner d'opération d'essai,
 * l'étape de poste, le pod, le visualiseur, l'affichage), dont **trois sont partagés avec les écrans de
 * production**. Y faire descendre un `essaiId` aurait ajouté une entrée à chacun — donc trois composants
 * de production modifiés pour un besoin de formulation, et une entrée que le prochain lecteur devra
 * comprendre à chaque étage.
 *
 * ⚠️ **C'est un contexte d'ÉCRAN, pas un état métier** : il ne porte aucune donnée, seulement « le cockpit
 * de tel essai est ouvert ». Le cockpit le pose en arrivant et le **retire en partant** — sans ce retrait,
 * un contrôle de production saisi ensuite partirait sur la route d'un essai. C'est la seule règle à tenir,
 * et elle est vérifiée par le `ngOnDestroy` de la page.
 *
 * ⚠️ **Un seul essai à la fois**, et c'est fidèle à l'écran : on n'ouvre pas deux cockpits dans le même
 * onglet. Une pile aurait laissé croire le contraire.
 */
@Injectable({ providedIn: "root" })
export class ContexteEssaiService {
  /** L'essai dont le cockpit est ouvert — `null` partout ailleurs dans l'application. */
  readonly essaiId = signal<string | null>(null);

  /**
   * **L'opération d'essai DÉPLIÉE**, quand il y en a une — le contexte du relevé qu'on s'apprête à saisir.
   *
   * ⚠️ **Sans elle, le rattachement d'un relevé à son opération repose entièrement sur la FENÊTRE DE
   * TEMPS** côté serveur (« quelle opération était ouverte à cet instant ? »). C'est un repli honnête, et
   * il reste en place — mais il se trompe dès que deux opérations se chevauchent, ce qui arrive sur une
   * cuisson relancée. Un relevé rattaché à la mauvaise manip est faux **sans en avoir l'air** : il y a
   * bien une opération, et bien un relevé.
   *
   * ⚠️ **Elle suit le DÉPLI, pas le clic sur le résumé** : un `<details>` se ferme par le même clic qui
   * l'ouvre, donc écouter le clic poserait l'opération au moment précis où on la quitte.
   *
   * ⚠️ **Rien n'interdit à deux opérations d'être dépliées** (le `<details>` n'est pas un accordéon) : la
   * dernière ouverte gagne, ce qui est celle sur laquelle on vient d'agir. Le cas est rare, et le repli
   * serveur reste là pour lui — c'est pourquoi le paramètre absent ne remet JAMAIS la colonne à `null`.
   */
  readonly operationEssaiId = signal<string | null>(null);

  ouvrir(essaiId: string | null): void {
    this.essaiId.set(essaiId ?? null);
  }

  /** Une opération vient d'être dépliée : c'est elle qui portera les relevés saisis. */
  ouvrirOperation(operationEssaiId: string): void {
    this.operationEssaiId.set(operationEssaiId);
  }

  /**
   * Une opération vient d'être repliée.
   *
   * ⚠️ **On ne referme que la SIENNE** : replier une opération pendant qu'une autre est ouverte ne doit
   * pas effacer le contexte de celle qui reste — sinon le geste le plus anodin de l'écran ferait retomber
   * les relevés suivants sur le repli serveur.
   */
  fermerOperation(operationEssaiId: string): void {
    this.operationEssaiId.set(operationApresRepli(this.operationEssaiId(), operationEssaiId));
  }

  fermer(): void {
    this.essaiId.set(null);
    this.operationEssaiId.set(null);
  }
}
